import { atom } from 'nanostores';

/**
 * The startup's live data in the editor (User Stories 596 and 598), between the host app and the
 * workbench. The host app (`@ideoz/web`) knows the startup and calls the Nest API; the workbench
 * owns the preview and hot edit.
 *
 * - `config`: the founder's preview token. The prototype's `src/lib/appStore.ts` (written by
 *   chef `appDataLayer.ts`) asks its parent for it (`ideoz:app-data:request`), and
 *   `useAppDataBridge` answers with this. `undefined` while the host is still getting it, `null`
 *   when the prototype has no live data (the preview keeps its sample rows).
 * - `liveEdit`: saves a hot edit of a row that is live data rather than a line in a data file.
 * - `changed`: the preview reloads that entity (or all of them), after an edit in the Data view
 *   or with hot edit.
 * - `openDataView`: bumped to switch the workbench to its Data view.
 */
export type AppDataPreviewConfig = { api: string; preview: string };

export type LiveRecordEdit = { recordId: string; field: string; newText: string };

export type LiveEditResult = { ok: true; entity: string } | { ok: false; error: string };

export const appDataStore = {
  config: atom<AppDataPreviewConfig | null | undefined>(undefined),
  liveEdit: atom<((edit: LiveRecordEdit) => Promise<LiveEditResult>) | null>(null),
  changed: atom<{ entity: string | null; at: number } | null>(null),
  openDataView: atom(0),
};

/** Tells the preview that the startup's live data changed (`entity` null: all of it). */
export function notifyAppDataChanged(entity: string | null): void {
  appDataStore.changed.set({ entity, at: Date.now() });
}

/** The words every place that edits live data shows (US 596). */
export const LIVE_DATA_NOTICE = 'Changes here are shared with everyone using your links.';
